import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ComponentType, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Row,
  Segmented,
  Space,
  Spin,
  Typography,
  App,
  theme,
} from 'antd';
import { HistoryOutlined, LeftOutlined, PlusOutlined, RightOutlined, SettingOutlined, ToolOutlined, CalendarOutlined, LineChartOutlined, BellOutlined } from '@ant-design/icons';
import { CategoryTagCloudFilter, PageHeader, PageContainer, withDevModalFilePath } from '../../components/common';
import { PermissionButton } from '../../components/common/PermissionButton';
import { usePermission } from '../../hooks/usePermission';
import { trpc } from '../../lib/api';
import moment from 'moment';
import 'moment/locale/ja';
import { Calendar as BigCalendar, momentLocalizer, View, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './Calendar.css';
import { WorkScheduleDetailModal, type WorkScheduleDetail } from '../../components/WorkSchedule/DetailModal';
import { WorkScheduleEditModal } from '../../components/WorkSchedule/EditModal';
import { useCalendarSchedules, useMutateWorkSchedule } from '../../hooks/useWorkSchedules';
import type { WorkScheduleStatus } from '../../../../shared/types/work-schedule.types';
import { HelpButton } from '../../components/help/HelpButton';

moment.locale('ja');

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/Maintenance/Calendar.tsx');
const localizer = momentLocalizer(moment);
// Type definitions of react-big-calendar do not expose a named drag-and-drop calendar component,
// so we cast here to bridge the generic event typing.
const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(
  BigCalendar as unknown as ComponentType<unknown>
);

type CalendarView = Exclude<View, 'agenda' | 'work_week'>;

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: ScheduleEventResource;
}

type CalendarSchedule = WorkScheduleDetail & {
  equipmentId?: string | null;
  isRecurringSource?: boolean;
  recurringScheduleId?: string | null;
};

type WorkTypeCategory = {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
};

type ScheduleEventResource = {
  id: string;
  title: string;
  displayId?: string;
  status: WorkScheduleStatus;
  priority?: string | null;
  scheduledDuration?: number | null;
  isRecurringSource?: boolean;
  recurringScheduleId?: string | null;
  scheduledDate?: string | Date;
  equipmentId?: string;
  raw: CalendarSchedule;
};

// STATUS_COLORS はコンポーネント内で token を使って動的に生成

const DEFAULT_STATUS_FILTER: WorkScheduleStatus[] = ['scheduled', 'in_progress', 'postponed', 'completed'];
const DEFAULT_DURATION_MINUTES = 60;

function computeRange(view: CalendarView, date: Date): { start: Date; end: Date } {
  const base = moment(date);
  switch (view) {
    case Views.MONTH: {
      const start = base.clone().startOf('month').startOf('week');
      const end = base.clone().endOf('month').endOf('week');
      return { start: start.toDate(), end: end.toDate() };
    }
    case Views.WEEK: {
      const start = base.clone().startOf('week');
      const end = base.clone().endOf('week');
      return { start: start.toDate(), end: end.toDate() };
    }
    case Views.DAY:
    default: {
      const start = base.clone().startOf('day');
      const end = base.clone().endOf('day');
      return { start: start.toDate(), end: end.toDate() };
    }
  }
}

function getEventDurationMinutes(start: Date, end: Date): number {
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  return diff > 0 ? diff : DEFAULT_DURATION_MINUTES;
}

function parseDate(value: string | Date | undefined | null): Date {
  if (!value) {
    return new Date();
  }
  if (value instanceof Date) {
    return value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

export function MaintenanceCalendarPage(): JSX.Element {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { canEdit } = usePermission();

  // ステータス色をテーマトークンから生成
  const STATUS_COLORS = useMemo((): Record<WorkScheduleStatus, { background: string; border: string }> => ({
    scheduled: { background: token.purple1 || token.colorPrimaryBg, border: token.purple || token.colorPrimary },
    pending_approval: { background: token.colorWarningBg, border: token.colorWarning },
    in_progress: { background: token.colorInfoBg, border: token.colorInfo },
    completed: { background: token.colorSuccessBg, border: token.colorSuccess },
    cancelled: { background: token.colorErrorBg, border: token.colorError },
    postponed: { background: token.colorWarningBg, border: token.colorWarning }
  }), [token]);

  const [view, setView] = useState<CalendarView>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [statusFilter] = useState<WorkScheduleStatus[]>(DEFAULT_STATUS_FILTER);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [editingSchedule, setEditingSchedule] = useState<CalendarSchedule | null>(null);
  const range = useMemo(() => computeRange(view, currentDate), [view, currentDate]);

  const {
    data: workTypeCategories = [],
    isLoading: isLoadingWorkTypeCategories,
    error: workTypeCategoriesError
  } = trpc.workTypeCategory.list.useQuery({});

  const workTypeCategoryList = useMemo(() => {
    const data = workTypeCategories as unknown;
    if (Array.isArray(data)) {
      return data as WorkTypeCategory[];
    }
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      if (Array.isArray(record.data)) {
        return record.data as WorkTypeCategory[];
      }
      if (Array.isArray(record.items)) {
        return record.items as WorkTypeCategory[];
      }
      if (Array.isArray(record.json)) {
        return record.json as WorkTypeCategory[];
      }
    }
    return [];
  }, [workTypeCategories]);

  // 設備一覧を取得（設備0件の場合の誘導表示に使用）
  const {
    data: equipmentsData,
    isLoading: isLoadingEquipments
  } = trpc.equipment.list.useQuery({ page: 1, limit: 1 });

  const hasEquipments = (equipmentsData?.pagination?.total ?? 0) > 0;

  // スケジュール総数を取得（全期間）
  const { data: allSchedulesCount } = trpc.workSchedule.list.useQuery(
    { page: 1, limit: 1 },
    { staleTime: 60000 }
  );
  const hasSchedules = (allSchedulesCount?.total ?? 0) > 0;

  const calendarQueryAll = useCalendarSchedules(
    {
      startDate: range.start,
      endDate: range.end,
      statuses: statusFilter
    },
    { placeholderData: keepPreviousData }
  );

  const calendarQueryFiltered = useCalendarSchedules(
    {
      startDate: range.start,
      endDate: range.end,
      statuses: statusFilter,
      workTypeCategoryId: selectedCategory ?? null
    },
    { enabled: Boolean(selectedCategory), placeholderData: keepPreviousData }
  );

  const schedulesDataAll = useMemo(
    () => {
      const data = calendarQueryAll.data as unknown;
      if (Array.isArray(data)) {
        return data as CalendarSchedule[];
      }
      if (data && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        if (Array.isArray(record.items)) {
          return record.items as CalendarSchedule[];
        }
        if (Array.isArray(record.data)) {
          return record.data as CalendarSchedule[];
        }
        if (Array.isArray(record.json)) {
          return record.json as CalendarSchedule[];
        }
      }
      return [];
    },
    [calendarQueryAll.data]
  );
  const schedulesDataFiltered = useMemo(
    () => {
      if (!selectedCategory) {
        return undefined;
      }
      const data = calendarQueryFiltered.data as unknown;
      if (Array.isArray(data)) {
        return data as CalendarSchedule[];
      }
      if (data && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        if (Array.isArray(record.items)) {
          return record.items as CalendarSchedule[];
        }
        if (Array.isArray(record.data)) {
          return record.data as CalendarSchedule[];
        }
        if (Array.isArray(record.json)) {
          return record.json as CalendarSchedule[];
        }
      }
      return [];
    },
    [calendarQueryFiltered.data, selectedCategory]
  );

  const resolveScheduleCategory = useCallback((schedule: CalendarSchedule) => {
    return schedule.workTypeCategoryId ?? schedule.workTypeCategory?.id ?? null;
  }, []);

  // カテゴリフィルタを適用
  const schedules = useMemo(() => {
    if (selectedCategory) {
      const filteredSource = schedulesDataFiltered ?? schedulesDataAll;
      if (!filteredSource) return undefined;
      return filteredSource.filter((s) => resolveScheduleCategory(s) === selectedCategory);
    }
    return schedulesDataAll;
  }, [selectedCategory, schedulesDataAll, schedulesDataFiltered, resolveScheduleCategory]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    (schedulesDataAll ?? []).forEach((schedule) => {
      const catId = resolveScheduleCategory(schedule);
      if (!catId) return;
      counts.set(catId, (counts.get(catId) ?? 0) + 1);
    });
    return counts;
  }, [schedulesDataAll, resolveScheduleCategory]);

  const schedulesError = calendarQueryFiltered.error ?? calendarQueryAll.error;
  const isLoadingSchedules = selectedCategory
    ? calendarQueryFiltered.isLoading || calendarQueryAll.isLoading
    : calendarQueryAll.isLoading;

  const refetchSchedules = useCallback(async () => {
    await Promise.all([
      calendarQueryAll.refetch(),
      selectedCategory ? calendarQueryFiltered.refetch() : Promise.resolve()
    ]);
  }, [calendarQueryAll, calendarQueryFiltered, selectedCategory]);

  const { updateSchedule: updateMutation } = useMutateWorkSchedule();
  const updateMutationPending =
    (updateMutation as { isPending?: boolean }).isPending ??
    (updateMutation as { isLoading?: boolean }).isLoading ??
    false;
  const isSpinning = isLoadingSchedules || updateMutationPending;

  const scheduleIndex = useMemo(() => {
    const map = new Map<string, CalendarSchedule>();
    (schedules ?? []).forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [schedules]);

  const selectedSchedule = selectedScheduleId ? scheduleIndex.get(selectedScheduleId) ?? null : null;

  const handleViewChange = useCallback((nextView: string | number) => {
    if (typeof nextView === 'string') {
      setView(nextView as CalendarView);
    }
  }, []);

  const handleNavigate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const handleToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditMode('create');
    setEditingSchedule(null);
    setEditModalOpen(true);
  }, []);

  const handleMove = useCallback(
    (args: { event: CalendarEvent; start: Date; end: Date }) => {
      const { event, start, end } = args;

      // 編集権限チェック
      if (!canEdit) {
        message.warning('閲覧権限のため、スケジュールの移動はできません');
        void refetchSchedules();
        return;
      }

      const status = event.resource.status;
      if (status === 'completed' || status === 'cancelled') {
        message.warning('完了済みまたはキャンセル済みのスケジュールは移動できません');
        void refetchSchedules();
        return;
      }

      const performUpdate = () => {
        updateMutation.mutate(
          {
            id: event.id,
            scheduledDate: start,
            scheduledDuration: getEventDurationMinutes(start, end)
          },
          {
            onSuccess: () => {
              message.success('スケジュールを更新しました');
              void refetchSchedules();
            },
            onError: (error) => {
              message.error(error?.message ?? 'スケジュールの更新に失敗しました');
              void refetchSchedules();
            }
          }
        );
      };

      const isRecurring =
        event.resource.isRecurringSource ||
        Boolean(event.resource.recurringScheduleId);

      if (isRecurring) {
        Modal.confirm({
          title: '繰り返しスケジュールの移動',
          content: 'このスケジュールは繰り返し設定が存在します。今回の予定のみ移動しますか？',
          okText: '移動する',
          cancelText: 'キャンセル',
          onOk: performUpdate,
          onCancel: () => {
            void refetchSchedules();
          }
        });
        return;
      }

      performUpdate();
    },
    [updateMutation, refetchSchedules, canEdit, message]
  );

  const events: CalendarEvent[] = useMemo(() => {
    if (!schedules) {
      return [];
    }

    return schedules.map((schedule) => {
      const start = parseDate(schedule.scheduledDate);
      const duration = schedule.scheduledDuration ?? DEFAULT_DURATION_MINUTES;
      const end = moment(start).add(duration, 'minutes').toDate();

      return {
        id: schedule.id,
        title: schedule.title ?? '無題のスケジュール',
        start,
        end,
        resource: {
          id: schedule.id,
          title: schedule.title ?? '',
          displayId: schedule.displayId,
          status: schedule.status,
          priority: schedule.priority,
          scheduledDuration: schedule.scheduledDuration,
          isRecurringSource: schedule.isRecurringSource,
          recurringScheduleId: schedule.recurringScheduleId,
          scheduledDate: schedule.scheduledDate,
          equipmentId: schedule.equipmentId,
          raw: schedule
        }
      };
    });
  }, [schedules]);

  const eventPropGetter = useCallback(
    (event: CalendarEvent) => {
      const meta = STATUS_COLORS[event.resource.status] ?? STATUS_COLORS.scheduled;
      return {
        style: {
          borderLeft: `4px solid ${meta.border}`,
          backgroundColor: meta.background,
          color: token.colorText,
          borderRadius: 6,
          paddingLeft: 8
        }
      };
    },
    [STATUS_COLORS, token.colorText]
  );

  // 日付セルのスタイル（今日、今月以外）
  const dayPropGetter = useCallback(
    (date: Date) => {
      const isToday = moment(date).isSame(moment(), 'day');
      const isCurrentMonth = moment(date).isSame(moment(currentDate), 'month');

      if (isToday) {
        return {
          style: {
            backgroundColor: token.colorPrimaryBg,
          }
        };
      }

      if (!isCurrentMonth) {
        return {
          style: {
            backgroundColor: token.colorFillSecondary,
            color: token.colorTextQuaternary,
          }
        };
      }

      return {};
    },
    [currentDate, token.colorPrimaryBg, token.colorFillSecondary, token.colorTextQuaternary]
  );

  const formattedPeriod = useMemo(() => {
    switch (view) {
      case Views.MONTH:
        return moment(currentDate).format('YYYY年 M月');
      case Views.WEEK: {
        const start = moment(range.start).format('M/D');
        const end = moment(range.end).format('M/D');
        return `${start} - ${end}`;
      }
      case Views.DAY: {
        const days = ['日', '月', '火', '水', '木', '金', '土'];
        return `${moment(currentDate).format('YYYY年 M月 D日')} (${days[moment(currentDate).day()]})`;
      }
      default:
        return '';
    }
  }, [view, currentDate, range.start, range.end]);

  const calendarHeight = useMemo(() => {
    if (view === Views.DAY) {
      return 720;
    }
    if (view === Views.WEEK) {
      return 680;
    }
    return 780;
  }, [view]);

  const calendarStyle = useMemo(() => ({
    height: calendarHeight,
    color: token.colorText,
    borderColor: token.colorBorder,
    '--calendar-overlay-bg': token.colorBgElevated,
    '--calendar-overlay-border': token.colorBorderSecondary ?? token.colorBorder,
    '--calendar-overlay-text': token.colorText,
    '--calendar-overlay-shadow': '0 8px 20px rgba(0, 0, 0, 0.15)',
  } as CSSProperties), [
    calendarHeight,
    token.colorBgElevated,
    token.colorBorder,
    token.colorBorderSecondary,
    token.colorText,
  ]);

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedScheduleId(event.id);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
  }, []);

  const handleDetailChanged = useCallback(async () => {
    await refetchSchedules();
  }, [refetchSchedules]);

  const handleDetailDeleted = useCallback(async () => {
    await refetchSchedules();
    setDetailOpen(false);
    setSelectedScheduleId(null);
  }, [refetchSchedules]);

  const handleRequestEdit = useCallback((scheduleToEdit: CalendarSchedule) => {
    if (!scheduleToEdit) {
      return;
    }
    setDetailOpen(false);
    setEditMode('edit');
    setEditingSchedule(scheduleToEdit);
    setEditModalOpen(true);
  }, []);

  const handleEditSuccess = useCallback(async () => {
    await refetchSchedules();
  }, [refetchSchedules]);

  useEffect(() => {
    if (schedulesError) {
      console.error('[MaintenanceCalendarPage] schedulesError', schedulesError);
    }
  }, [schedulesError]);

  useEffect(() => {
    if (workTypeCategoriesError) {
      console.error('[MaintenanceCalendarPage] workTypeCategoriesError', workTypeCategoriesError);
    }
  }, [workTypeCategoriesError]);

  return (
    <PageContainer>
      <PageHeader
        title="保全カレンダー"
        subtitle={
          <Space size="small">
            <HelpButton topicId="maintenance-calendar" size="small" position="inline" />
          </Space>
        }
        extra={
          <>
            <PermissionButton
              icon={<SettingOutlined />}
              onClick={() => navigate('/app/categories#work-type')}
              title="作業カテゴリ管理"
              requiredPermission="manage"
            >
              カテゴリ管理
            </PermissionButton>
            <PermissionButton
              icon={<HistoryOutlined />}
              onClick={() => navigate('/app/maintenance/recurring-settings')}
              title="繰り返し設定"
              requiredPermission="manage"
            >
              繰り返し設定
            </PermissionButton>
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              requiredPermission="edit"
            >
              新規スケジュール
            </PermissionButton>
          </>
        }
      />

        {/* スケジュールが0件かつ設備が0件の場合 - 設備登録への誘導バナー */}
        {!isLoadingEquipments && !hasSchedules && !hasEquipments && (
          <Card
            style={{
              background: token.colorWarningBg,
              border: `1px solid ${token.colorWarningBorder}`,
            }}
          >
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <ToolOutlined style={{ fontSize: 32, color: token.colorWarning }} />
              </Col>
              <Col flex={1}>
                <Title level={5} style={{ marginBottom: 4 }}>
                  まず設備を登録しましょう
                </Title>
                <Text type="secondary">
                  設備を登録すると、以下の機能が利用できるようになります。
                </Text>
              </Col>
              <Col>
                <Button type="primary" onClick={() => navigate('/app/equipments')}>
                  設備を登録する
                </Button>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                  <Space>
                    <CalendarOutlined style={{ fontSize: 20, color: token.purple }} />
                    <div>
                      <Text strong>保全スケジュール</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        点検・メンテナンス計画を立てて管理
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                  <Space>
                    <LineChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                    <div>
                      <Text strong>健全度予測</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        設備の劣化状況を予測・把握
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                  <Space>
                    <BellOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                    <div>
                      <Text strong>通知・アラート</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        点検時期の通知を自動で受け取る
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
        )}

        {/* カテゴリタグクラウドフィルタ */}
        <Card>
          <CategoryTagCloudFilter
            categories={workTypeCategoryList.map((cat) => ({
              id: cat.id,
              categoryLabel: cat.categoryLabel,
              color: cat.color,
              icon: cat.icon,
              count: categoryCounts.get(cat.id) ?? 0,
            }))}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            loading={
              !workTypeCategoryList.length &&
              (isLoadingWorkTypeCategories || isLoadingSchedules)
            }
            showCounts={true}
            maxVisibleTags={15}
          />
        </Card>

        {/* カレンダーナビゲーションバー */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space size="small">
              <Button
                icon={<LeftOutlined />}
                onClick={() => handleNavigate(moment(currentDate).subtract(1, view).toDate())}
              />
              <Button onClick={handleToday}>今日</Button>
              <Button
                icon={<RightOutlined />}
                onClick={() => handleNavigate(moment(currentDate).add(1, view).toDate())}
              />
            </Space>
          </Col>
          <Col>
            <Title level={4} style={{ margin: 0 }}>{formattedPeriod}</Title>
          </Col>
          <Col>
            <Segmented
              value={view}
              options={[
                { label: '月', value: Views.MONTH },
                { label: '週', value: Views.WEEK },
                { label: '日', value: Views.DAY }
              ]}
              onChange={handleViewChange}
            />
          </Col>
        </Row>

        <Card styles={{ body: { padding: 0, borderColor: token.colorBorder } }}>
          <Spin spinning={isSpinning}>
            <div style={calendarStyle}>
              <DragAndDropCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                view={view}
                date={currentDate}
                onView={handleViewChange}
                onNavigate={handleNavigate}
                toolbar={false}
                selectable
                resizable
                popup
                onEventDrop={handleMove}
                onEventResize={handleMove}
                onSelectEvent={handleSelectEvent}
                onDoubleClickEvent={handleSelectEvent}
                eventPropGetter={eventPropGetter}
                dayPropGetter={dayPropGetter}
                messages={{
                  month: '月',
                  week: '週',
                  day: '日',
                  today: '今日',
                  previous: '前へ',
                  next: '次へ',
                  showMore: (total) => `他 ${total} 件`
                }}
                formats={{
                  monthHeaderFormat: (date) => moment(date).format('YYYY年M月'),
                  weekdayFormat: (date) => {
                    const days = ['日', '月', '火', '水', '木', '金', '土'];
                    return days[moment(date).day()];
                  },
                  dayFormat: (date) => moment(date).format('D日'),
                  dayHeaderFormat: (date) => {
                    const days = ['日', '月', '火', '水', '木', '金', '土'];
                    return `${moment(date).format('M月D日')} (${days[moment(date).day()]})`;
                  },
                  dayRangeHeaderFormat: ({ start, end }) =>
                    `${moment(start).format('M月D日')} - ${moment(end).format('M月D日')}`,
                  eventTimeRangeFormat: ({ start, end }) =>
                    `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`
                }}
              />
            </div>
          </Spin>
        </Card>
      <WorkScheduleDetailModal
        open={detailOpen}
        schedule={selectedSchedule}
        onClose={handleCloseDetail}
        onEdit={selectedSchedule ? handleRequestEdit : undefined}
        onChanged={handleDetailChanged}
        onDeleted={handleDetailDeleted}
      />
      <WorkScheduleEditModal
        open={editModalOpen}
        mode={editMode}
        schedule={editMode === 'edit' ? editingSchedule : null}
        defaultDate={currentDate}
        onClose={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </PageContainer>
  );
}



